import React from 'react';
import { HStack, Text, Pressable } from '@gluestack-ui/themed';

export type SegmentedPillOption = {
  label: string;
  value: string;
  testID?: string;
};

export type SegmentedPillProps = {
  options: SegmentedPillOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  testID?: string;
  /** When false, options size to label text instead of splitting width evenly. */
  equalWidth?: boolean;
};

/**
 * SegmentedPill
 * ----
 * Presentational pill-style segmented control (module progression policy look).
 * Form-connected usage goes through SegmentedToggle.
 */
export function SegmentedPill({
  options,
  value,
  onChange,
  disabled = false,
  testID,
  equalWidth = true,
}: SegmentedPillProps) {
  return (
    <HStack
      bg="$bgSubtle"
      p={3}
      borderRadius="$lg"
      alignSelf={equalWidth ? 'stretch' : 'flex-start'}
      borderWidth={1}
      borderColor="$borderLight"
      testID={testID}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <Pressable
            key={option.value}
            onPress={() => {
              if (!disabled) onChange(option.value);
            }}
            flex={equalWidth ? 1 : undefined}
            py="$1.5"
            px={equalWidth ? undefined : '$2'}
            borderRadius="$md"
            bg={isActive ? '$brandBase' : 'transparent'}
            opacity={disabled ? 0.6 : 1}
            alignItems="center"
            testID={option.testID}
            accessibilityRole="button"
            accessibilityState={{ selected: isActive, disabled }}
          >
            <Text
              size="xs"
              color={isActive ? '$textInverted' : '$textSubtle'}
              fontWeight="semibold"
              numberOfLines={1}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </HStack>
  );
}
